import { z } from 'zod';
import { ContactIdSchema, EmailSchema, HistoryEntrySchema, IDSchema, IsoTimestampSchema, MsaTemplateVersionSchema, QuoteIdSchema, SchemaVersionSchema, } from './shared.js';
export const SubmissionStateSchema = z.enum([
    'draft',
    'sent',
    'accepted',
    'declined',
    'expired',
    'withdrawn',
    // RIG-500 — replaced by a revised proposal. Terminal, and deliberately
    // distinct from `withdrawn`: withdrawn means the rep pulled the proposal and
    // nothing replaced it; superseded means a newer proposal carries the work
    // forward and is linked via `supersededBySubmissionId`.
    'superseded',
]);
export const SubmissionSchema = z.object({
    id: IDSchema,
    type: z.literal('submission'),
    schemaVersion: SchemaVersionSchema,
    accountId: IDSchema,
    state: SubmissionStateSchema,
    stateChangedAt: IsoTimestampSchema,
    stateChangedBy: z.string(),
    repEmail: EmailSchema,
    submittedAt: IsoTimestampSchema,
    submittedBy: z.string(),
    sentToClientAt: IsoTimestampSchema.optional(),
    sentToClientBy: z.string().optional(),
    clientEmailSubject: z.string().optional(),
    clientEmailBody: z.string().optional(),
    clientEmailRecipients: z.array(z.string()).default([]),
    acceptedQuoteId: QuoteIdSchema.optional(),
    acceptedAt: IsoTimestampSchema.optional(),
    // T5-008 — How the acceptance was captured (dual entry points per
    // Kenny 2026-05-27 email-first communication model).
    // - `customer_click`: customer clicked the tokenized Accept link in
    //   the proposal_send email (public route, no Entra auth).
    // - `rep_marked`: rep recorded the acceptance on the customer's behalf
    //   (rep-authenticated route, verbal-confirmation note in audit).
    // - `staff_recorded` (RIG-655): a Super Admin recorded by hand an
    //   acceptance the customer gave another way (phone, email reply).
    acceptedVia: z.enum(['customer_click', 'rep_marked', 'staff_recorded']).optional(),
    closureReason: z.string().optional(),
    closedAt: IsoTimestampSchema.optional(),
    closedBy: z.string().optional(),
    dealId: IDSchema.optional(),
    // RIG-500 — the supersede chain. `supersededBySubmissionId` is set on the
    // proposal that was replaced; `supersedesSubmissionId` on the one that
    // replaced it.
    supersededBySubmissionId: IDSchema.optional(),
    supersedesSubmissionId: IDSchema.optional(),
    // T-MSA-CONTENT-PIPELINE (per-submission MSA version tracking) — the MSA
    // template version stamped at proposal-send time (draft → sent). Read by
    // the customer-facing MSA review surface so a re-opened review link resolves
    // the version the customer is about to sign, not a newer global default.
    // Optional: legacy rows created before the column existed are absent; the
    // reader falls back to the current default version for those.
    msaTemplateVersion: MsaTemplateVersionSchema.optional(),
    expiryFiredAt: IsoTimestampSchema.optional(),
    // Order-flow review (fix-now 8) — the proposal's one validity: 30 days from
    // the day its email is sent (reset by "Send accept link"). Absent until sent.
    expiresAt: IsoTimestampSchema.optional(),
    // T5-013 — Stamped at the moment the 3d-before-expire reminder fires.
    // The submission-expiry cron's REMINDER-phase predicate is `IS NULL`, so
    // this column is the idempotency anchor for the reminder fire.
    expiryReminderFiredAt: IsoTimestampSchema.optional(),
    history: z.array(HistoryEntrySchema).default([]),
    // RIG-380 — human-readable reference (e.g. `PROP-00087`). Additive layer over
    // the opaque PK; NOT NULL + unique. Render via the shared display helpers.
    reference: z.string(),
    createdAt: IsoTimestampSchema,
    createdBy: z.string(),
    updatedAt: IsoTimestampSchema,
    updatedBy: z.string(),
    deletedAt: IsoTimestampSchema.nullable().optional(),
    version: z.number().int().positive(),
});
/**
 * Order-flow review (fix-now 8) — POST /api/v1/submissions/:id/send-accept-link.
 * The one rep action on a sent, still-open proposal: re-send the proposal
 * email with fresh accept/decline links and a fresh 30-day validity, revoking
 * every earlier link. `contactId` picks any active contact with an email on
 * the account; omitted, the email goes to the original recipient.
 */
export const SendAcceptLinkRequestSchema = z
    .object({
    expectedVersion: z.number().int().positive(),
    contactId: ContactIdSchema.optional(),
})
    .strict();
export const SendAcceptLinkResponseSchema = z.object({
    submission: SubmissionSchema,
    sentTo: z.string(),
    expiresAt: IsoTimestampSchema,
    revokedTokenCount: z.number().int().nonnegative(),
});
//# sourceMappingURL=submission.js.map